import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, PencilSimple, ChatCircle, ClockCounterClockwise, Check, Trash, Files,
  ImageBroken, ArrowsClockwise, ListChecks, Coins, CheckCircle, Prohibit,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import MarcheStatusBadge from './MarcheStatusBadge';
import DpgfPosteSelector from './DpgfPosteSelector';
import DevisComplementaireDialog from './DevisComplementaireDialog';
import { BOUTONS, STATUT_ACTION, TYPE_ELEMENT } from '../../lib/libelles';
import EpinglePlan from './plans/EpinglePlan'; // A4 — action sur le plan
import usePermissions from '../../hooks/usePermissions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  reserve:             { label: 'Réserve',         color: '#C75B39' },
  tache:               { label: TYPE_ELEMENT.tache, color: '#1E3A5F' },
  incident:            { label: 'Incident',        color: '#C00000' },
  non_conformite:      { label: 'Non-conformité',  color: '#8B3A3A' },
  demande_client:      { label: 'Demande client',  color: '#7B5EA7' },
  question_entreprise: { label: 'Question',        color: '#5B8FB9' },
  observation:         { label: 'Observation',     color: '#6B6B6B' },
  validation:          { label: 'Validation',      color: '#2D6A4F' },
  decision:            { label: 'Décision',        color: '#1B4965' },
  info:                { label: 'Info',            color: '#9A9A9A' },
};

const TYPES = Object.keys(TYPE_CONFIG);

const PRIORITES = [
  { key: 'urgent', label: 'Urgent',   color: '#C00000' },
  { key: 'normal', label: 'Normal',   color: '#1E3A5F' },
  { key: 'basse',  label: 'Basse',    color: '#9A9A9A' },
];

// Fluidité A3 (constat 24) — l'historique parle la langue de l'utilisateur, pas celle de la base.
const ACTIONS_HISTORIQUE = {
  cree: 'Création', created: 'Création', cree_depuis_message: 'Création depuis le Fil',
  cree_via_taches: 'Création', cree_via_reserves: 'Création (réserve)', cree_via_points_crc: 'Création (compte rendu)',
  cree_amo_dpgf_comment: "Création (remarque de l'AMO)",
  modifie: 'Modification', enriched_from_tache: 'Complétée', fusionnee: 'Fusion avec une autre action',
  migre_depuis_tache: 'Reprise', migre_depuis_reserve: 'Reprise (réserve)',
  valide_par_archi: 'Validée par la maîtrise d\'œuvre', cloture_par_archi: 'Levée par la maîtrise d\'œuvre',
  levee_proposee: 'Preuve envoyée : levée à vérifier', levee_validee: 'Levée validée', levee_refusee: 'Levée refusée',
  levee_en_reunion: 'Levée en réunion', reportee_en_reunion: 'Reportée en réunion', transferee: 'Transférée',
  commentaire_ajoute: 'Commentaire', dpgf_lignes_attach: 'Rattachée au marché', dpgf_ligne_detach: 'Détachée du marché',
  devis_complementaire_demande: 'Devis complémentaire demandé', synced: 'Planning mis à jour',
  error: "Analyse de l'Assistant indisponible",
};
const CHAMPS_LISIBLES = {
  titre: 'titre', description: 'description', entreprise: 'entreprise', entreprise_user_ids: 'entreprise',
  lot: 'lot', priorite: 'priorité', echeance: 'échéance', duree_jours: 'durée', lieu: 'lieu',
  inclure_dans_prochain_crc: 'compte rendu', type: 'type', etat: 'état', visibilite: 'visibilité',
};
function libelleHistorique(action) {
  if (!action) return 'Modification';
  const k = String(action).toLowerCase();
  if (ACTIONS_HISTORIQUE[k]) return ACTIONS_HISTORIQUE[k];
  if (k.startsWith('etat_') && STATUT_ACTION[k.slice(5)]) return `État : ${STATUT_ACTION[k.slice(5)]}`;
  const texte = k.replace(/_/g, ' ');
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

function dateCourte(iso) {
  const s = String(iso || '');
  const d = new Date(s.length <= 10 ? `${s}T12:00:00` : s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

function estReserveReception(e) {
  return !!e && (e.categorie === 'reserve_reception' || !!e.reception_id);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function DetailsPanel({ element, chantierId, onUpdate, onClose, onRemove }) {
  const eid = element._id || element.id;
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const [marches, setMarches] = useState([]);

  const [type, setType] = useState(element.type || 'info');
  const [titre, setTitre] = useState(element.titre || '');
  const [description, setDescription] = useState(element.description || element.texte_brut || '');
  const [entreprise, setEntreprise] = useState(element.entreprise || '');
  const [lot, setLot] = useState(element.lot || '');
  const [priorite, setPriorite] = useState(element.priorite || 'normal');
  const [echeance, setEcheance] = useState(element.echeance || '');
  const [dureeJours, setDureeJours] = useState(element.duree_jours || 1);
  const [lieu, setLieu] = useState(element.lieu || '');
  const [inclureCRC, setInclureCRC] = useState(element.inclure_dans_prochain_crc !== false);
  const { role_projet: roleProjet } = usePermissions(chantierId); // A4 — placer sur le plan (MOE)

  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true })
      .then((r) => setMarches(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMarches([]));
  }, [chantierId]);

  const lotsUnique = [...new Set(marches.map((m) => m.nom).filter(Boolean))];
  const entreprisesUnique = [...new Set(marches.map((m) => m.entreprise_nom).filter(Boolean))];

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/elements/${eid}`, {
        type, titre: titre.slice(0, 200), description: description || null,
        entreprise: entreprise || null, lot: lot || null,
        priorite, echeance: echeance || null,
        duree_jours: parseInt(dureeJours, 10) || 1,
        lieu: lieu || null, inclure_dans_prochain_crc: inclureCRC,
      }, { withCredentials: true });
      toast.success('Modifications enregistrées');
      setEditing(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEtat = async (newEtat, label) => {
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/elements/${eid}/etat`, { etat: newEtat }, { withCredentials: true });
      toast.success(label);
      if (newEtat === 'ignore' || newEtat === 'fait') {
        onRemove?.(eid);
        onClose?.();
      } else {
        onUpdate?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  // RP16 — « Valider / Refuser la levée » (action « À vérifier », preuve apportée)
  const handleLevee = async (valider) => {
    let body = {};
    if (!valider) {
      const motif = window.prompt('Motif du refus (transmis à l\'entreprise) :');
      if (motif === null) return;
      if (!motif.trim()) {
        toast.warning('Indiquez le motif du refus');
        return;
      }
      body = { motif: motif.trim() };
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/elements/${eid}/${valider ? 'valider-levee' : 'refuser-levee'}`,
        body,
        { withCredentials: true },
      );
      toast.success(valider ? 'Levée validée : elle figurera au prochain compte rendu' : "Levée refusée : l'action repasse à faire");
      onRemove?.(eid);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const preuves = Array.isArray(element.preuves_entreprise) ? element.preuves_entreprise : [];

  const cfg = TYPE_CONFIG[element.type] || TYPE_CONFIG.info;

  if (editing) {
    return (
      <div className="space-y-4 p-4 text-sm" data-testid="drawer-edit-form">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Type</label>
          <div className="flex flex-wrap gap-1">
            {TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`px-2 py-1 text-xs border rounded-none ${type === t ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white text-[#2C2C2C] border-[#E0E0DB]'}`}
                data-testid={`drawer-edit-type-${t}`}
              >
                {TYPE_CONFIG[t].label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Titre</label>
          <input
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            maxLength={200}
            className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm"
            data-testid="drawer-edit-titre"
          />
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Description</label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="rounded-none"
            data-testid="drawer-edit-description"
          />
        </div>

        {entreprisesUnique.length > 0 && (
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Entreprise</label>
            <div className="flex flex-wrap gap-1">
              {entreprisesUnique.map((e) => (
                <button
                  key={e}
                  onClick={() => setEntreprise(entreprise === e ? '' : e)}
                  className={`px-2 py-1 text-xs border rounded-none ${entreprise === e ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white text-[#2C2C2C] border-[#E0E0DB]'}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        )}

        {lotsUnique.length > 0 && (
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Lot</label>
            <div className="flex flex-wrap gap-1">
              {lotsUnique.map((l) => (
                <button
                  key={l}
                  onClick={() => setLot(lot === l ? '' : l)}
                  className={`px-2 py-1 text-xs border rounded-none ${lot === l ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white text-[#2C2C2C] border-[#E0E0DB]'}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Priorité</label>
          <div className="flex gap-2">
            {PRIORITES.map((p) => (
              <button
                key={p.key}
                onClick={() => setPriorite(p.key)}
                className={`flex-1 px-3 py-2 text-xs font-semibold border rounded-none ${priorite === p.key ? 'text-white' : ''}`}
                style={{
                  background: priorite === p.key ? p.color : 'white',
                  color: priorite === p.key ? 'white' : p.color,
                  borderColor: p.color,
                }}
                data-testid={`drawer-edit-priorite-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Échéance</label>
            <input
              type="date"
              value={echeance}
              onChange={(e) => setEcheance(e.target.value)}
              className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm"
              data-testid="drawer-edit-echeance"
            />
          </div>
          <div className="w-20">
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Durée (j)</label>
            <input
              type="number"
              min="1"
              max="60"
              value={dureeJours}
              onChange={(e) => setDureeJours(e.target.value)}
              className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm"
              data-testid="drawer-edit-duree"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">Lieu</label>
          <input
            value={lieu}
            onChange={(e) => setLieu(e.target.value)}
            className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm"
            data-testid="drawer-edit-lieu"
          />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={inclureCRC}
            onChange={(e) => setInclureCRC(e.target.checked)}
            data-testid="drawer-edit-crc"
          />
          Inclure dans le prochain compte rendu
        </label>

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1 rounded-none"
            onClick={() => setEditing(false)}
            disabled={submitting}
            data-testid="drawer-edit-cancel"
          >
            Annuler
          </Button>
          <Button
            className="flex-1 rounded-none bg-[#2D6A4F] hover:bg-[#1f5639] text-white"
            onClick={handleSave}
            disabled={submitting}
            data-testid="drawer-edit-save"
          >
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 text-sm" data-testid="drawer-details-readonly">
      {/* Type + priorité */}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-none"
          style={{ background: cfg.color + '15', color: cfg.color }}
          data-testid="drawer-type-badge"
        >
          {cfg.label}
        </span>
        {element.priorite === 'urgent' && (
          <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white bg-[#C00000]">⚡ Urgent</span>
        )}
        {element.source_id === 'visite' && (
          <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#C4773B] bg-[#FDF6EC]">🎥 Visite</span>
        )}
        {element.marche_status && element.marche_status !== 'pre_marche' && (
          <MarcheStatusBadge status={element.marche_status} testId="drawer-marche-status" />
        )}
        {/* Lot A1 — état de l'analyse prêt à afficher (« IA indisponible : à trier à la main »…) */}
        {(element.ia_libelle || element.a_trier) && (
          <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#4B5563] bg-[#F5F5F3]" data-testid="drawer-etat-ia">
            {element.ia_libelle || 'À trier à la main'}
          </span>
        )}
        {estReserveReception(element) && (
          <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#C75B39] bg-[#FDF1E1]" data-testid="drawer-reserve-reception">
            Réserve de réception
          </span>
        )}
      </div>

      {/* Photo */}
      {element.media_url && !imgBroken && (
        <img
          src={element.media_url}
          alt=""
          onError={() => setImgBroken(true)}
          className="w-full max-h-64 object-cover cursor-zoom-in"
          onClick={() => window.open(element.media_url, '_blank')}
          data-testid="drawer-media"
        />
      )}
      {element.media_url && imgBroken && (
        <div className="text-xs text-[#9A9A9A] flex items-center gap-1"><ImageBroken size={14} /> Média indisponible</div>
      )}

      {/* A4 — position sur le plan : vignette + « Voir sur le plan », ou « Placer sur le plan » */}
      <div className="-mx-3.5">
        <EpinglePlan chantierId={chantierId} element={element} onChange={onUpdate}
          peutPlacer={['archi', 'moe_principal', 'moe_membre'].includes(roleProjet) && !['fait', 'ignore'].includes(element.etat)} />
      </div>

      {/* Description */}
      {element.description && (
        <div className="text-sm text-[#2C2C2C] whitespace-pre-wrap leading-relaxed" data-testid="drawer-description">{element.description}</div>
      )}
      {!element.description && element.texte_brut && (
        <div className="text-sm text-[#2C2C2C] whitespace-pre-wrap leading-relaxed">{element.texte_brut}</div>
      )}

      {/* Métadonnées table */}
      <dl className="text-xs grid grid-cols-2 gap-x-3 gap-y-2 border-t border-[#E0E0DB] pt-3 mt-2">
        <dt className="text-[#9A9A9A] uppercase tracking-wider font-semibold">Entreprise</dt>
        <dd className="text-[#2C2C2C]">{element.entreprise || '—'}</dd>
        <dt className="text-[#9A9A9A] uppercase tracking-wider font-semibold">Lot</dt>
        <dd className="text-[#2C2C2C]">{element.lot || '—'}</dd>
        <dt className="text-[#9A9A9A] uppercase tracking-wider font-semibold">Échéance</dt>
        <dd className="text-[#2C2C2C]">{element.echeance ? dateCourte(element.echeance) : '—'}</dd>
        <dt className="text-[#9A9A9A] uppercase tracking-wider font-semibold">Durée</dt>
        <dd className="text-[#2C2C2C]">{element.duree_jours || 1}j</dd>
        <dt className="text-[#9A9A9A] uppercase tracking-wider font-semibold">Lieu</dt>
        <dd className="text-[#2C2C2C]">{element.lieu || '—'}</dd>
        <dt className="text-[#9A9A9A] uppercase tracking-wider font-semibold">Inclure dans le CR</dt>
        <dd className="text-[#2C2C2C]">{element.inclure_dans_prochain_crc !== false ? 'Oui' : 'Non'}</dd>
        {/* Fluidité A3 (constat 24) — plus de champs techniques (confiance, fournisseur d'IA) */}
      </dl>

      {/* RP15-16 — Preuves apportées par l'entreprise + dernier refus */}
      {element.etat === 'actif' && element.levee_refusee_motif && (
        <div className="text-xs border-l-4 border-[#C00000] bg-[#FDF2F2] p-2 text-[#8B2C2C]" data-testid="drawer-levee-refusee">
          <strong>Levée refusée</strong> : {element.levee_refusee_motif}
        </div>
      )}
      {preuves.length > 0 && (
        <div className="border-t border-[#E0E0DB] pt-3 space-y-2" data-testid="drawer-preuves">
          <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-bold">
            Preuves de l&apos;entreprise ({preuves.length})
          </div>
          {preuves.slice().reverse().map((p, i) => (
            <div key={`${p.at}-${i}`} className="flex gap-2 text-xs">
              {p.photo_url && (
                <img
                  src={p.photo_url}
                  alt="Preuve"
                  className="w-16 h-16 object-cover cursor-zoom-in flex-shrink-0"
                  onClick={() => window.open(p.photo_url, '_blank')}
                />
              )}
              <div className="min-w-0">
                {p.commentaire && <div className="whitespace-pre-wrap text-[#2C2C2C]">{p.commentaire}</div>}
                <div className="text-[#9A9A9A]">{p.by_user_label || 'Entreprise'} · {fmtDate(p.at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-[#E0E0DB] mt-2">
        <Button
          variant="outline"
          className="rounded-none"
          onClick={() => setEditing(true)}
          data-testid="drawer-btn-edit"
        >
          <PencilSimple size={14} className="mr-1" /> Modifier
        </Button>
        {element.etat === 'a_verifier' ? (
          <>
            <Button
              className="rounded-none bg-[#2D6A4F] hover:bg-[#245740] text-white"
              onClick={() => handleLevee(true)}
              disabled={submitting}
              data-testid="drawer-btn-valider-levee"
            >
              <CheckCircle size={14} className="mr-1" /> {BOUTONS.validerLevee}
            </Button>
            <Button
              variant="outline"
              className="rounded-none border-[#8B2C2C] text-[#8B2C2C] hover:bg-[#8B2C2C] hover:text-white col-span-2"
              onClick={() => handleLevee(false)}
              disabled={submitting}
              data-testid="drawer-btn-refuser-levee"
            >
              <Prohibit size={14} className="mr-1" /> {BOUTONS.refuserLevee}
            </Button>
          </>
        ) : element.etat === 'actif' ? (
          <>
            <Button
              variant="outline"
              className="rounded-none border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white"
              onClick={() => handleEtat('fait', 'Action levée')}
              disabled={submitting}
              data-testid="drawer-btn-fait"
            >
              <Check size={14} className="mr-1" /> Marquer levée
            </Button>
            <Button
              variant="outline"
              className="rounded-none border-[#9A9A9A] text-[#9A9A9A] hover:bg-[#9A9A9A] hover:text-white col-span-2"
              onClick={() => handleEtat('ignore', STATUT_ACTION.ignore)}
              disabled={submitting}
              data-testid="drawer-btn-ignorer"
            >
              <Trash size={14} className="mr-1" /> Sans suite
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            className="rounded-none border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
            onClick={() => handleEtat('actif', 'Réactivé')}
            disabled={submitting}
            data-testid="drawer-btn-reactiver"
          >
            <ArrowsClockwise size={14} className="mr-1" /> Réactiver
          </Button>
        )}
      </div>
    </div>
  );
}

function DpgfPanel({ chantierId, element, onUpdate }) {
  const eid = element._id || element.id;
  const [showDevisDialog, setShowDevisDialog] = useState(false);
  return (
    <div className="p-4 space-y-4 text-sm" data-testid="drawer-dpgf-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <MarcheStatusBadge status={element.marche_status} testId="drawer-dpgf-status" />
        <span className="text-xs text-[#6B6B6B]">
          {(element.lignes_dpgf_ids || []).length} ligne{(element.lignes_dpgf_ids || []).length > 1 ? 's' : ''} rattachée{(element.lignes_dpgf_ids || []).length > 1 ? 's' : ''}
        </span>
      </div>

      {element.devis_complementaire_id && (
        <div className="border-l-4 border-[#C4773B] bg-[#FDF1E1] p-3 text-xs text-[#8B4513]" data-testid="drawer-devis-comp-info">
          <div className="font-bold uppercase tracking-wider text-[10px] mb-1">Devis complémentaire en cours</div>
          Une demande de chiffrage hors marché est associée à cette action.
        </div>
      )}

      <DpgfPosteSelector
        chantierId={chantierId}
        elementId={eid}
        lignesActuelles={element.lignes_dpgf_ids || []}
        onSaved={(updated) => onUpdate?.(updated)}
      />

      <div className="border-t border-[#E0E0DB] pt-3">
        <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-bold mb-2">Hors marché ?</div>
        <Button
          variant="outline"
          className="rounded-none w-full border-[#C4773B] text-[#C4773B] hover:bg-[#C4773B] hover:text-white"
          onClick={() => setShowDevisDialog(true)}
          data-testid="drawer-dpgf-devis-comp-btn"
        >
          <Coins size={14} className="mr-1" /> Demander un devis complémentaire
        </Button>
      </div>

      <DevisComplementaireDialog
        open={showDevisDialog}
        onClose={() => setShowDevisDialog(false)}
        chantierId={chantierId}
        element={element}
        onCreated={() => onUpdate?.()}
      />
    </div>
  );
}

function HistoryPanel({ historique }) {
  if (!historique || historique.length === 0) {
    return <div className="p-6 text-center text-sm text-[#9A9A9A]" data-testid="drawer-history-empty">Aucun historique.</div>;
  }
  const sorted = [...historique].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  return (
    <ol className="p-4 space-y-3 text-sm" data-testid="drawer-history-list">
      {sorted.map((h, i) => (
        <li key={i} className="border-l-2 border-[#E0E0DB] pl-3 py-1">
          <div className="font-semibold text-[#2C2C2C]">{libelleHistorique(h.action)}</div>
          <div className="text-xs text-[#6B6B6B]">{fmtDate(h.at)}</div>
          {(h.by_label || h.by_name) && <div className="text-xs text-[#9A9A9A]">par {h.by_label || h.by_name}</div>}
          {!h.by_label && !h.by_name && h.by === 'system' && <div className="text-xs text-[#9A9A9A]">par l&apos;Assistant</div>}
          {h.fields && (
            <div className="text-xs text-[#6B6B6B] mt-1">champs : {(h.fields || []).map((f) => CHAMPS_LISIBLES[f] || f).join(', ')}</div>
          )}
          {h.type_detecte && (
            <div className="text-xs text-[#6B6B6B] mt-1">type proposé : <span className="font-semibold">{TYPE_CONFIG[h.type_detecte]?.label || h.type_detecte}</span></div>
          )}
        </li>
      ))}
    </ol>
  );
}

function CommentsPanel({ chantierId, elementId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/elements/${elementId}/comments`,
        { withCredentials: true },
      );
      setComments(Array.isArray(data) ? data : []);
    } catch (e) {
      // 404 silencieux : feature optionnelle non encore exposée par le backend
      if (e?.response?.status !== 404) toast.error('Erreur chargement commentaires');
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (elementId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId]);

  const send = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/elements/${elementId}/comments`,
        { content: text.trim() },
        { withCredentials: true },
      );
      setText('');
      load();
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.info('Commentaires : fonctionnalité à venir (Session 5/6)');
      } else {
        toast.error(e?.response?.data?.detail || 'Erreur envoi');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 space-y-3 text-sm" data-testid="drawer-comments-panel">
      {loading ? (
        <div className="text-center text-[#9A9A9A] py-6">Chargement…</div>
      ) : comments.length === 0 ? (
        <div className="text-center text-[#9A9A9A] py-6 text-xs italic">Aucun commentaire encore.</div>
      ) : (
        <ul className="space-y-3">
          {comments.map((c, i) => (
            <li key={i} className="border border-[#E0E0DB] p-2">
              <div className="text-xs text-[#9A9A9A]">{c.author_name || 'Anonyme'} · {fmtDate(c.created_at)}</div>
              <div className="text-sm whitespace-pre-wrap mt-1">{c.content}</div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ajouter un commentaire…"
          className="rounded-none"
          data-testid="drawer-comment-input"
        />
        <Button
          onClick={send}
          disabled={submitting || !text.trim()}
          className="rounded-none bg-[#1E3A5F] hover:bg-[#152a48]"
          data-testid="drawer-comment-send"
        >
          Envoyer
        </Button>
      </div>
    </div>
  );
}

export default function ElementDetailDrawer({ element, chantierId, open, onClose, onUpdate, onRemove }) {
  const [tab, setTab] = useState('details');

  useEffect(() => { if (open) setTab('details'); }, [open, element?._id, element?.id]);

  if (!open || !element) return null;
  const eid = element._id || element.id;
  const titre = element.titre || element.texte_brut || '(sans titre)';

  return (
    <div
      className="fixed inset-0 z-50 flex"
      onKeyDown={(e) => e.key === 'Escape' && onClose?.()}
      data-testid="element-detail-drawer"
    >
      {/* Overlay */}
      <div
        className="flex-1 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="drawer-overlay"
      />
      {/* Panel */}
      <div className="w-full max-w-md sm:max-w-lg bg-white shadow-2xl flex flex-col h-full" data-testid="drawer-panel">
        {/* Header */}
        <div className="border-b border-[#E0E0DB] p-4 flex items-start gap-3 bg-[#FAFAF8]">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#2C2C2C] truncate" data-testid="drawer-titre">{titre}</h2>
            <p className="text-xs text-[#9A9A9A] mt-0.5">
              {fmtDate(element.created_at)} · {element.created_by_name || 'Inconnu'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#E0E0DB] rounded-none flex-shrink-0"
            aria-label="Fermer"
            data-testid="drawer-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#E0E0DB] flex">
          {[
            { key: 'details', label: 'Détails', icon: Files },
            { key: 'dpgf', label: 'Marché', icon: ListChecks },
            { key: 'comments', label: 'Commentaires', icon: ChatCircle },
            { key: 'history', label: 'Historique', icon: ClockCounterClockwise },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
                tab === t.key
                  ? 'border-[#C4773B] text-[#C4773B] bg-white'
                  : 'border-transparent text-[#9A9A9A] hover:text-[#6B6B6B]'
              }`}
              data-testid={`drawer-tab-${t.key}`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-testid="drawer-content">
          {tab === 'details' && (
            <DetailsPanel
              element={element}
              chantierId={chantierId}
              onUpdate={onUpdate}
              onClose={onClose}
              onRemove={onRemove}
            />
          )}
          {tab === 'dpgf' && (
            <DpgfPanel
              element={element}
              chantierId={chantierId}
              onUpdate={onUpdate}
            />
          )}
          {tab === 'comments' && <CommentsPanel chantierId={chantierId} elementId={eid} />}
          {tab === 'history' && <HistoryPanel historique={element.historique} />}
        </div>
      </div>
    </div>
  );
}
